import {
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import COLOR from "../../../constant/COLOR";
import IMAGES from "../../../constant/IMAGES";
import {
  getCalendarPerpetual,
  getCalendarPerpetualPDF,
} from "../../../service";
import CustomButtonV2 from "../ButtonExport";
import CalendarContent from "../CalenderContent";
import { CalendarHeader } from "../HeaderCalendar";
import HTMLFlipBook from "react-pageflip";
import { CaretDownOutlined } from "@ant-design/icons";
import CalendarPDFPage from "../CalenderContentPDF";
import { Document, PDFViewer, pdf } from "@react-pdf/renderer";

const { RangePicker } = DatePicker;

type ExportMode = "day" | "range" | "month" | "year" | "custom_day";

const EXPORT_OPTIONS: { key: ExportMode; label: string }[] = [
  { key: "day", label: "Hôm nay" },
  { key: "custom_day", label: "Chọn ngày cụ thể" },
  { key: "range", label: "Từ ngày - đến ngày" },
  { key: "month", label: "Tháng này" },
  { key: "year", label: "Năm nay" },
];

type DateRange = [dayjs.Dayjs | null, dayjs.Dayjs | null];

export default function CalendarMainPDF(): React.ReactElement {
  const toast = useToast();
  const today = dayjs();

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [detailCalendar, setDetailCalendar] = useState<any[]>([]);
  const [detailCalendarPDF, setDetailCalendarPDF] = useState<any[]>([]);

  const [exportMode, setExportMode] = useState<ExportMode>("day");
  const [dateRange, setDateRange] = useState<DateRange>([today, today]);
  const [startDate, endDate] = dateRange;

  // Bật/tắt preview PDF
  const [showPreview, setShowPreview] = useState(false);

  const fetchCalendarRange = async (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
    const results: any[] = [];
    let current = start.clone();

    while (current.isBefore(end) || current.isSame(end, "day")) {
      const chunkEnd = current.endOf("month");
      const finalEnd = chunkEnd.isAfter(end) ? end : chunkEnd;

      const res = await getCalendarPerpetualPDF(
        current.format("YYYY-MM-DD"),
        finalEnd.format("YYYY-MM-DD"),
        true,
        1,
      );

      const data = Array.isArray(res) ? res : res?.data || [];
      results.push(...data);

      current = finalEnd.add(1, "day");
    }

    return results;
  };

  const fetchCalendarPDFRange = async (
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
  ) => {
    const results: any[] = [];
    let current = start.clone();

    while (current.isBefore(end) || current.isSame(end, "day")) {
      const chunkEnd = current.endOf("month");
      const finalEnd = chunkEnd.isAfter(end) ? end : chunkEnd;

      const res = await getCalendarPerpetualPDF(
        current.format("YYYY-MM-DD"),
        finalEnd.format("YYYY-MM-DD"),
        true,
        2,
      );

      const data = Array.isArray(res) ? res : res?.data || [];
      results.push(...data);

      current = finalEnd.add(1, "day");
    }

    return results;
  };

  useEffect(() => {
    if (!startDate || !endDate) return;

    const fetchCalendar = async () => {
      try {
        setLoading(true);
        const data = await fetchCalendarRange(startDate, endDate);
        setDetailCalendar(data);
      } catch (error) {
        toast({
          title: "Lỗi tải dữ liệu",
          description: "Không thể lấy dữ liệu lịch.",
          status: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    const fetchCalendarPDF = async () => {
      try {
        setLoading(true);
        const data = await fetchCalendarPDFRange(startDate, endDate);
        setDetailCalendarPDF(data);
      } catch (error) {
        toast({
          title: "Lỗi tải dữ liệu",
          description: "Không thể lấy dữ liệu lịch.",
          status: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCalendar();
    fetchCalendarPDF();
  }, [startDate, endDate]);

  const handleChangeMode = (mode: ExportMode) => {
    setExportMode(mode);

    const now = dayjs();

    if (mode === "day") {
      setDateRange([now, now]);
    }

    if (mode === "custom_day") {
      setDateRange([now, now]);
    }

    if (mode === "month") {
      setDateRange([now.startOf("month"), now.endOf("month")]);
    }

    if (mode === "year") {
      setDateRange([now.startOf("year"), now.endOf("year")]);
    }
  };

  const buildExportRange = (): DateRange => {
    const now = dayjs();

    let start = now;
    let end = now;

    if (exportMode === "month") {
      start = now.startOf("month");
      end = now.endOf("month");
    }

    if (exportMode === "year") {
      start = now.startOf("year");
      end = now.endOf("year");
    }

    if (exportMode === "custom_day") {
      start = startDate ?? now;
      end = startDate ?? now;
    }

    if (exportMode === "range") {
      start = startDate ?? now;
      end = endDate ?? now;
    }

    return [start, end];
  };

  // ===== Preview PDF =====
  const handlePreview = () => {
    const [start, end] = buildExportRange();
    setDateRange([start, end]);
    setShowPreview(true);
  };

  // ===== Download PDF =====
  const handleDownload = async () => {
    if (!detailCalendarPDF.length) {
      toast({
        title: "Chưa có dữ liệu",
        description: "Vui lòng chọn khoảng thời gian trước khi tải PDF.",
        status: "warning",
      });
      return;
    }

    try {
      setDownloading(true);

      const doc = (
        <Document>
          {detailCalendarPDF.map((item, index) => (
            <CalendarPDFPage key={index} detailCalendar={item} />
          ))}
        </Document>
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "lich-van-nien.pdf";
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Lỗi xuất PDF",
        description: "Không thể tạo file PDF.",
        status: "error",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleQuickExport = async () => {
    const [start, end] = buildExportRange();
    setDateRange([start, end]);

    // Đợi data load xong rồi mới download
    // useEffect ở trên sẽ tự fetch lại detailCalendarPDF khi dateRange đổi
    // nên ta cần trigger download sau khi data đã sẵn sàng -> dùng flag
    setPendingDownload(true);
  };

  // Flag để biết cần tự động download sau khi fetch xong
  const [pendingDownload, setPendingDownload] = useState(false);

  useEffect(() => {
    if (!pendingDownload || loading) return;
    if (!detailCalendarPDF.length) return;

    handleDownload();
    setPendingDownload(false);
  }, [pendingDownload, loading, detailCalendarPDF]);

  return (
    <div className="relative h-screen bg-[#f4efe6] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${IMAGES.bgCalendarP})` }}
      />

      <div className="relative z-10 flex flex-col h-full">
        <CalendarHeader />

        <div className="flex justify-center gap-3 mt-3 flex-wrap">
          <Select
            value={exportMode}
            style={{ width: 220 }}
            onChange={handleChangeMode}
            suffixIcon={<CaretDownOutlined style={{ fontSize: 14 }} />}
            options={EXPORT_OPTIONS.map((item) => ({
              value: item.key,
              label: item.label,
            }))}
          />

          {exportMode === "custom_day" && (
            <DatePicker
              value={startDate}
              format="DD/MM/YYYY"
              onChange={(date) => {
                if (!date) {
                  setDateRange([today, today]);
                } else {
                  setDateRange([date, date]);
                }
              }}
            />
          )}

          {exportMode === "range" && (
            <RangePicker
              value={dateRange}
              format="DD/MM/YYYY"
              onChange={(dates) => {
                if (!dates || (!dates[0] && !dates[1])) {
                  setDateRange([today, today]);
                } else {
                  setDateRange(dates as any);
                }
              }}
            />
          )}

          <Menu>
            <MenuButton as="div">
              <CustomButtonV2
                text={downloading ? "Đang tạo PDF..." : "In / Lưu PDF"}
                variant="red"
              />
            </MenuButton>

            <MenuList>
              <MenuItem onClick={handlePreview}>Xem trước PDF</MenuItem>
              <MenuItem onClick={handleQuickExport} isDisabled={downloading}>
                Tải PDF
              </MenuItem>
            </MenuList>
          </Menu>
        </div>

        <div className="p-4 overflow-y-auto">
          <div className="flex-1 flex items-center justify-center mt-1 origin-top scale-[0.33] sm:scale-[0.6] md:scale-[0.8] lg:scale-100">
            {loading ? (
              <Flex justify="center" align="center" h="200px">
                <Spinner size="xl" color={COLOR.primary400} thickness="4px" />
              </Flex>
            ) : (
              <HTMLFlipBook
                width={1000}
                height={930}
                size="fixed"
                autoSize={false}
                usePortrait={true}
                showCover={false}
                drawShadow={true}
                flippingTime={600}
                mobileScrollSupport={false}
                className="shadow-xl"
                startPage={0}
                startZIndex={0}
                clickEventForward={true}
                useMouseEvents={true}
                swipeDistance={30}
                showPageCorners={true}
                disableFlipByClick={false}
                maxShadowOpacity={0.5}
                minWidth={1000}
                maxWidth={1000}
                minHeight={930}
                style={{}}
                maxHeight={930}
              >
                {detailCalendar.map((item, index) => (
                  <div key={index} className="bg-white">
                    <CalendarContent detailCalendar={item} />
                  </div>
                ))}
              </HTMLFlipBook>
            )}
          </div>
        </div>
      </div>

      {/* ===== PDF Preview Modal ===== */}
      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-lg w-[90vw] h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center px-4 py-2 border-b">
              <span className="font-semibold">Xem trước PDF</span>
              <div className="flex gap-2">
                <CustomButtonV2
                  text={downloading ? "Đang tạo..." : "Tải PDF"}
                  variant="red"
                  onClick={handleDownload}
                  disabled={downloading}
                />
                <button
                  className="px-3 py-1 rounded border"
                  onClick={() => setShowPreview(false)}
                >
                  Đóng
                </button>
              </div>
            </div>

            <div className="flex-1">
              {loading ? (
                <Flex justify="center" align="center" h="100%">
                  <Spinner size="xl" color={COLOR.primary400} thickness="4px" />
                </Flex>
              ) : detailCalendarPDF.length > 0 ? (
                <PDFViewer width="100%" height="100%" showToolbar={true}>
                  <Document>
                    {detailCalendarPDF.map((item, index) => (
                      <CalendarPDFPage key={index} detailCalendar={item} />
                    ))}
                  </Document>
                </PDFViewer>
              ) : (
                <Flex justify="center" align="center" h="100%">
                  <span>Không có dữ liệu để hiển thị.</span>
                </Flex>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
